import React, { MouseEvent, ReactNode, useEffect, useState } from 'react';

import ContactHelper from '../core/libs/ContactHelper';
import Image from '../core/Image';
import ContactsSpinner from './ContactsSpinner';
import DeletePopup from './modals/DeletePopup';
import pencil from '../core/assets/pencil.png';
import trash from '../core/assets/trash.png';
import { useContactsContext } from '../contexts/ContactsContext';
import {
  DeleteIcon,
  EditIcon,
  ListEmail,
  ListFullName,
  ListPhone,
  ListRowItem,
  ListRowItemColumn,
  ListRowItemIcons,
  ListRowItemIconsColumn,
  ListRowItemMultipleFields,
  ListRowItemWrapper
} from './styled/ContactsList';

import { TAppContact } from 'TProtocol/common/models';

interface Props {
  contact: TAppContact;
}

export default function ContactListRowItem({ contact }: Props) {
  const contactsContext = useContactsContext();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otherInfo, setOtherInfo] = useState<ReactNode[]>([]);
  const [showDelete, setShowDelete] = useState(false);
  const [showMenu, setShowMenu] = useState(true);

  const onDeleteClose = () => {
    setShowDelete(false);
    setShowMenu(true);
  };

  const onSelect = () => {
    contactsContext.setSelectedContact(contact);
  };

  const onEdit = (event: MouseEvent) => {
    contactsContext.setContactToEdit(contact);
    event.stopPropagation();
  }

  const onEmailClick = (event: MouseEvent) => {
    event.stopPropagation();
  };

  const onDelete = (event: MouseEvent) => {
    setShowMenu(false);
    setShowDelete(true);
    event.stopPropagation();
  };

  const doDelete = () => {
    setShowDelete(false);
    setShowMenu(true);
    contactsContext.onDelete(contact);
  };

  const cancelDelete = () => {
    setShowDelete(false);
    setShowMenu(true);
  };


  const returnEmpty = () => {
    return <></>;
  };

  useEffect(() => {
    updateData();
  }, [contact]);


  const updateData = () => {

    setEmail('');
    setPhone('');
    setOtherInfo([]);

    if (!contact.name) {
      returnEmpty();
    }

    let firstEmailField = '';
    if (contact.personalEmail?.displayEmail) {
      setEmail(contact.personalEmail.displayEmail);
      firstEmailField = 'personalEmail';
    } else if (contact.professionalEmail?.displayEmail) {
      setEmail(contact.professionalEmail.displayEmail);
      firstEmailField = 'professionalEmail';
    } else if (contact.otherEmails?.[0]?.displayEmail) {
      setEmail(contact.otherEmails[0].displayEmail);
      firstEmailField = 'otherEmails';
    }
    let firstPhoneField = '';
    if (contact.primaryMobile?.displayNumber) {
      setPhone(contact.primaryMobile.displayNumber);
      firstPhoneField = 'primaryMobile';
    } else if (contact.personalMobile?.displayNumber) {
      setPhone(contact.personalMobile.displayNumber);
      firstPhoneField = 'personalMobile';
    } else if (contact.personalLandline?.displayNumber) {
      setPhone(contact.personalLandline.displayNumber);
      firstPhoneField = 'personalLandline';
    } else if (contact.professionalMobile?.displayNumber) {
      setPhone(contact.professionalMobile.displayNumber);
      firstPhoneField = 'professionalMobile';
    } else if (contact.professionalLandline?.displayNumber) {
      setPhone(contact.professionalLandline.displayNumber);
      firstPhoneField = 'professionalLandline';
    } else if (contact.otherPhones?.[0]?.displayNumber) {
      setPhone(contact.otherPhones[0].displayNumber);
      firstPhoneField = 'otherPhones';
    }

    const jobInfo = [];
    if (contact.primaryProfessionalInfo?.jobTitle) {
      jobInfo.push(contact.primaryProfessionalInfo.jobTitle);
    }
    if (contact.primaryProfessionalInfo?.companyName) {
      jobInfo.push(contact.primaryProfessionalInfo.companyName);
    }

    const birthday = ContactHelper.toDateString(contact);

    let address = '';
    if (contact.personalAddress) {
      address = ContactHelper.addressString(contact.personalAddress);
    } else if (contact.professionalAddress) {
      address = ContactHelper.addressString(contact.professionalAddress);
    } else if (contact.otherAddresses && contact.otherAddresses.length > 0) {
      address = ContactHelper.addressString(contact.otherAddresses[0]);
    }

    const tempOtherInfo = [];

    if (firstPhoneField === 'primaryMobile' && (contact.personalMobile?.displayNumber)) {
      tempOtherInfo.push(contact.personalMobile.displayNumber);
    }
    if (firstPhoneField != 'personalLandline' && (contact.personalLandline?.displayNumber)) {
      tempOtherInfo.push(contact.personalLandline.displayNumber);
    }
    if (firstPhoneField !== 'professionalMobile' && (contact.professionalMobile?.displayNumber)) {
      tempOtherInfo.push(contact.professionalMobile.displayNumber);
    }
    if (firstPhoneField !== 'professionalLandline' && (contact.professionalLandline?.displayNumber)) {
      tempOtherInfo.push(contact.professionalLandline.displayNumber);
    }
    if (firstEmailField === 'personalEmail' && (contact.professionalEmail?.displayEmail)) {
      const email = contact.professionalEmail.displayEmail;
      if (email) {
        tempOtherInfo.push(<a href={'mailto:' + email} onClick={onEmailClick}>{email}</a>);
      }
    }
    if (address.length > 0) {
      tempOtherInfo.push(address);
    }
    if (jobInfo.length > 0) {
      tempOtherInfo.push(jobInfo.join(', '));
    }
    if (birthday) {
      tempOtherInfo.push(birthday);
    }

    setOtherInfo(tempOtherInfo);
  };

  return (
    <ListRowItemWrapper>
      <ListRowItem onClick={onSelect}>
        <ListRowItemColumn xs={4} md={4} xl={3}>
          <Image src={contact.photoUrl ?? ''} hideOnMobile={true} showSpinner={true}/>
          <ListFullName>{ContactHelper.getName(contact)}</ListFullName>
        </ListRowItemColumn>
        <ListRowItemColumn xs={5} md={3}>
          <ListRowItemMultipleFields>
            <ListPhone>{phone}</ListPhone>
            { email && <ListEmail onClick={onEmailClick}><a href={'mailto:' + email}>{email}</a></ListEmail> }
          </ListRowItemMultipleFields>
        </ListRowItemColumn>
        <ListRowItemColumn xs={3} xl={5} $hideOnMobile={true}>
          <ListRowItemMultipleFields>
            {otherInfo
              .map((item, idx) => <span key={idx}>{item}</span>)
              .reduce((acc, x) => acc === null ? [x] : [acc, ' - ', x], null)
            }
          </ListRowItemMultipleFields>
        </ListRowItemColumn>
        <ListRowItemIconsColumn>
          {contactsContext.updatingContact?.contactId === contact.contactId ?
            <ContactsSpinner/> :
            <div>
              {
                showMenu ? <ListRowItemIcons>
                  <EditIcon alt="Edit" src={pencil} onClick={onEdit}/>
                  <DeleteIcon alt="Delete" src={trash} onClick={onDelete}/>
                </ListRowItemIcons> : null
              }
              {
                showDelete ?
                  <DeletePopup onClose={onDeleteClose} doDelete={doDelete} cancelDelete={cancelDelete}/> : null
              }
            </div>
          }
        </ListRowItemIconsColumn>
      </ListRowItem>
    </ListRowItemWrapper>
  );

}
